import { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  usePurchaseOrders,
  useCreatePurchaseOrder,
  useUpdatePurchaseOrder,
  useDeletePurchaseOrder,
  useReceivePurchaseOrder,
} from '@/hooks/usePurchaseOrders';
import { useVendors } from '@/hooks/useVendors';
import { useRequisitions } from '@/hooks/useRequisitions';
import { useToast } from '@/hooks/useToast';
import { POItemsTab } from '@/components/purchase/POItemsTab';
import { POTotalsSummary } from '@/components/purchase/POTotalsSummary';
import { ReceivingModal } from '@/components/purchase/ReceivingModal';
import { POStatusBadge, canTransitionTo, getStatusTransitionLabel, type POStatus } from '@/components/purchase/POStatusBadge';
import type { PurchaseOrder } from '@/lib/schemas';
import type { POItemFormState, POFormState } from '@/types/purchase-order-form';
import { cn, formatCurrency } from '@/lib/utils';
import {
  Plus,
  FileText,
  Send,
  CheckCircle,
  Search,
  AlertTriangle,
  Calendar,
  Package,
  Scale,
  Clock,
  X,
  Copy,
  ArrowRight,
  MoreVertical,
  Pencil,
  Trash2,
  ClipboardList,
  View,
} from 'lucide-react';

// Default Terms and Conditions for PO
const DEFAULT_TERMS = `1. Payment Terms: Net 30 days from invoice date.
2. Delivery: Delivery shall be made within the agreed timeframe. Late delivery may result in order cancellation.
3. Quality: All items must meet the specified quality standards. Substandard items will be rejected.
4. Warranty: Supplier warrants that all items are free from defects and fit for the intended purpose.
5. Returns: Defective items may be returned within 14 days of receipt.
6. Liability: Supplier shall be liable for any damages arising from defective products.`;

const statusConfig = {
  draft: { variant: 'default' as const, label: 'Draft' },
  pending_approval: { variant: 'warning' as const, label: 'Pending Approval' },
  approved: { variant: 'info' as const, label: 'Approved' },
  ordered: { variant: 'info' as const, label: 'Ordered' },
  partial: { variant: 'warning' as const, label: 'Partial' },
  received: { variant: 'success' as const, label: 'Received' },
  cancelled: { variant: 'error' as const, label: 'Cancelled' },
};

export default function PurchaseOrdersPage() {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);
  const [deletingPO, setDeletingPO] = useState<PurchaseOrder | null>(null);
  const [receivingPO, setReceivingPO] = useState<PurchaseOrder | null>(null);
  const [showConvertRequisition, setShowConvertRequisition] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState<string>('');
  const [viewingPO, setViewingPO] = useState<PurchaseOrder | null>(null);

  // Form state for PO with items
  const [poFormItems, setPoFormItems] = useState<POItemFormState[]>([]);
  const [hospitalId] = useState<string>('550e8400-e29b-41d4-a716-446655440000');

  // Form field states
  const [formHospitalId, setFormHospitalId] = useState<string>('1');
  const [formVendorId, setFormVendorId] = useState<string>('');
  const [formOrderDate, setFormOrderDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [formDeliveryDate, setFormDeliveryDate] = useState<string>('');
  const [formNotes, setFormNotes] = useState<string>('');
  const [formTerms, setFormTerms] = useState<string>('');

  // Keyboard shortcut handler
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
      e.preventDefault();
      setIsCreateOpen(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const { data: purchaseOrdersResponse, isLoading, error } = usePurchaseOrders();
  const purchaseOrders = purchaseOrdersResponse?.data || [];
  const { data: vendorsResponse } = useVendors();
  const vendors = vendorsResponse?.data || [];
  const { data: requisitionsResponse } = useRequisitions({ limit: 100 });
  const requisitions = requisitionsResponse?.data || [];
  const createMutation = useCreatePurchaseOrder();
  const updateMutation = useUpdatePurchaseOrder();
  const deleteMutation = useDeletePurchaseOrder();
  const receiveMutation = useReceivePurchaseOrder();

  const approvedRequisitions = requisitions.filter(r => r.status === 'approved');

  const statusOptions = Object.entries(statusConfig).map(([value, { label }]) => ({
    value,
    label,
  }));

  const filteredPOs = useMemo(() => {
    return purchaseOrders.filter((po) => {
      const matchesSearch =
        (po.po_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        getVendorName(po.vendor_id).toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || po.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [purchaseOrders, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    purchaseOrders.forEach((po) => {
      counts.all++;
      counts[po.status] = (counts[po.status] || 0) + 1;
    });
    return counts;
  }, [purchaseOrders]);

  const getVendorName = (vendorId: string) => {
    const vendor = vendors.find((v) => v.vendor_id === vendorId);
    return vendor?.name || 'Unknown Vendor';
  };

  const vendorOptions = vendors
    .filter((v) => !!v.vendor_id)
    .map((v) => ({
      value: v.vendor_id!,
      label: `${v.code || ''} - ${v.name}`,
    }));

  // Reset form items when modal closes
  useEffect(() => {
    if (!isCreateOpen && !editingPO) {
      setPoFormItems([]);
    }
  }, [isCreateOpen, editingPO]);

  // Load existing PO data when editing
  useEffect(() => {
    if (editingPO) {
      const items: POItemFormState[] = editingPO.items.map(item => ({
        item_id: item.item_id,
        item_code: item.item_code || item.item_id,
        item_name: item.item_name || `Item ${item.item_id}`,
        quantity_ordered: item.quantity_ordered,
        unit_price: item.unit_price,
        unit_of_measure: item.unit_of_measure || 'each',
        line_total: item.line_total || (item.quantity_ordered * item.unit_price),
        available_quantity: item.available_quantity,
      }));
      setPoFormItems(items);
      setFormHospitalId(editingPO.hospital_id);
      setFormVendorId(editingPO.vendor_id);
      setFormOrderDate(editingPO.order_date || new Date().toISOString().split('T')[0]);
      setFormDeliveryDate(editingPO.delivery_date || '');
      setFormNotes(editingPO.notes || '');
      setFormTerms(editingPO.terms || '');
    } else {
      // Reset form when closing modal
      setFormHospitalId('550e8400-e29b-41d4-a716-446655440000');
      setFormVendorId('');
      setFormOrderDate(new Date().toISOString().split('T')[0]);
      setFormDeliveryDate('');
      setFormNotes('');
      setFormTerms(DEFAULT_TERMS);
      setPoFormItems([]);
    }
  }, [editingPO]);

  const handleCreate = async (formData: POFormState) => {
    const totalAmount = poFormItems.reduce((sum, item) => sum + item.line_total, 0);
    // Auto-submit for approval if amount exceeds threshold (e.g., 100000)
    const requiresApproval = totalAmount > 100000;
    
    try {
      await createMutation.mutateAsync({
        hospital_id: formData.hospital_id,
        vendor_id: formData.vendor_id,
        order_date: formData.order_date,
        delivery_date: formData.delivery_date,
        notes: formData.notes,
        terms: formTerms || DEFAULT_TERMS,
        items: poFormItems.map(item => ({
          item_id: item.item_id,
          quantity_ordered: item.quantity_ordered,
          unit_price: item.unit_price,
          line_total: item.line_total,
          quantity_received: 0,
        })),
        status: requiresApproval ? 'pending_approval' : 'draft',
        traceability_level: 'batch',
        total_amount: totalAmount,
      });
      toast.success('Purchase order created', requiresApproval 
        ? 'PO has been submitted for approval.' 
        : 'Purchase order has been created.');
      setIsCreateOpen(false);
      setPoFormItems([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create purchase order';
      toast.error('Error creating PO', message);
      console.error('Failed to create PO:', error);
    }
  };

  const handleUpdate = async (formData: POFormState) => {
    if (!editingPO?.po_id) return;

    const totalAmount = poFormItems.reduce((sum, item) => sum + item.line_total, 0);
    const requiresApproval = totalAmount > 100000;
    
    try {
      await updateMutation.mutateAsync({
        id: editingPO.po_id,
        data: {
          hospital_id: formData.hospital_id,
          vendor_id: formData.vendor_id,
          order_date: formData.order_date,
          delivery_date: formData.delivery_date,
          notes: formData.notes,
          terms: formTerms || DEFAULT_TERMS,
          items: poFormItems.map(item => ({
            item_id: item.item_id,
            quantity_ordered: item.quantity_ordered,
            unit_price: item.unit_price,
            line_total: item.line_total,
            quantity_received: 0,
          })),
          traceability_level: editingPO.traceability_level,
          total_amount: totalAmount,
          status: requiresApproval && editingPO.status === 'draft' ? 'pending_approval' : editingPO.status,
        },
      });
      toast.success('Purchase order updated', 'Your changes have been saved.');
      setEditingPO(null);
      setPoFormItems([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update purchase order';
      toast.error('Error updating PO', message);
      console.error('Failed to update PO:', error);
    }
  };

  const handleReceive = async (poId: string, receiveData: { items: Array<{ po_item_id: string; quantity_received: number; batch_number?: string; expiry_date?: string }> }) => {
    try {
      await receiveMutation.mutateAsync({ id: poId, receiveData });
      toast.success('PO received', 'Items have been marked as received.');
      setReceivingPO(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to receive items';
      toast.error('Error receiving items', message);
      console.error('Failed to receive items:', error);
    }
  };

  const handleDelete = async () => {
    if (deletingPO && deletingPO.po_id) {
      try {
        await deleteMutation.mutateAsync(deletingPO.po_id);
        toast.success('PO deleted', `Purchase order ${deletingPO.po_number} has been deleted.`);
        setDeletingPO(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete purchase order';
        toast.error('Error deleting PO', message);
        console.error('Failed to delete PO:', error);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Purchase Orders
          </h1>
          <p className="text-text-secondary mt-1">
            Track and manage purchase orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowConvertRequisition(true)}>
            <ArrowRight className="mr-2 h-4 w-4" />
            Convert Requisition
          </Button>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create PO
            <span className="ml-2 text-xs text-white/60 hidden sm:inline">(Ctrl+N)</span>
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-900">Error loading purchase orders</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { label: 'All', value: stats.all, status: 'all' as const, icon: FileText, color: 'bg-blue-100 text-blue-700' },
          { label: 'Draft', value: stats.draft || 0, status: 'draft' as const, icon: FileText, color: 'bg-gray-100 text-gray-700' },
          { label: 'Ordered', value: stats.ordered || 0, status: 'ordered' as const, icon: Send, color: 'bg-cyan-100 text-cyan-700' },
          { label: 'Partial', value: stats.partial || 0, status: 'partial' as const, icon: Package, color: 'bg-yellow-100 text-yellow-700' },
          { label: 'Received', value: stats.received || 0, status: 'received' as const, icon: CheckCircle, color: 'bg-green-100 text-green-700' },
          { label: 'Cancelled', value: stats.cancelled || 0, status: 'cancelled' as const, icon: AlertTriangle, color: 'bg-red-100 text-red-700' },
        ].map(({ label, value, status: statStatus, icon: Icon, color }) => (
          <Card
            key={label}
            className={cn(
              'cursor-pointer transition-all',
              statusFilter === statStatus ? 'ring-2 ring-primary' : ''
            )}
            onClick={() => setStatusFilter(statStatus === 'all' ? 'all' : statusFilter === statStatus ? 'all' : statStatus)}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={cn('p-2 rounded-lg', color)}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-text-primary">{value}</p>
                  <p className="text-sm text-text-muted">{label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search by PO number or vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                aria-label="Search purchase orders"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={10} columns={7} />
      ) : filteredPOs.length > 0 ? (
        <DataTable
          columns={[
            {
              key: 'po_number',
              header: 'PO Number',
              render: (po: PurchaseOrder) => (
                <div>
                  <p className="font-medium text-text-primary">{po.po_number || 'N/A'}</p>
                  <p className="text-xs text-text-muted flex items-center gap-1 mt-1">
                    <Calendar className="h-3 w-3" />
                    {po.created_at ? new Date(po.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              ),
            },
            {
              key: 'vendor_id',
              header: 'Vendor',
              render: (po: PurchaseOrder) => (
                <span className="text-text-secondary">{getVendorName(po.vendor_id)}</span>
              ),
            },
            {
              key: 'items',
              header: 'Items',
              render: (po: PurchaseOrder) => (
                <div className="text-sm">
                  <p className="font-medium">{po.items.length} item(s)</p>
                  <p className="text-text-muted text-xs">
                    {po.items.reduce((sum, item) => sum + item.quantity_ordered, 0)} total quantity
                  </p>
                </div>
              ),
            },
            {
              key: 'total_amount',
              header: 'Total',
              render: (po: PurchaseOrder) => (
                <span className="font-mono text-text-primary font-semibold">
                  {formatCurrency(po.total_amount)}
                </span>
              ),
            },
            {
              key: 'delivery_date',
              header: 'Delivery',
              render: (po: PurchaseOrder) => {
                if (!po.delivery_date) return <span className="text-text-muted">-</span>;
                const date = new Date(po.delivery_date);
                const isOverdue = date < new Date() && po.status !== 'received';
                return (
                  <span className={cn(isOverdue ? 'text-red-600 font-semibold' : 'text-text-secondary')}>
                    {date.toLocaleDateString()}
                  </span>
                );
              },
            },
            {
              key: 'status',
              header: 'Status',
              render: (po: PurchaseOrder) => (
                <POStatusBadge status={po.status as POStatus} />
              ),
            },
            {
              key: 'actions',
              header: 'Actions',
              render: (po: PurchaseOrder) => (
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setViewingPO(po)}
                    title="View Details"
                  >
                    <View className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingPO(po);
                    }}
                    disabled={po.status === 'received' || po.status === 'cancelled'}
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const items: POItemFormState[] = po.items.map(item => ({
                        item_id: item.item_id,
                        item_code: item.item_code || item.item_id,
                        item_name: item.item_name || `Item ${item.item_id}`,
                        quantity_ordered: item.quantity_ordered,
                        unit_price: item.unit_price,
                        unit_of_measure: item.unit_of_measure || 'each',
                        line_total: item.line_total || (item.quantity_ordered * item.unit_price),
                        available_quantity: item.available_quantity,
                      }));
                      setPoFormItems(items);
                      setFormHospitalId(po.hospital_id);
                      setFormVendorId(po.vendor_id);
                      setFormOrderDate(new Date().toISOString().split('T')[0]);
                      setFormDeliveryDate(po.delivery_date || '');
                      setFormNotes(po.notes || '');
                      setFormTerms(po.terms || DEFAULT_TERMS);
                      setIsCreateOpen(true);
                      toast.success('PO duplicated', 'PO items have been copied. You can edit before saving.');
                    }}
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setReceivingPO(po)}
                    disabled={['draft', 'cancelled', 'received'].includes(po.status)}
                    title="Receive Items"
                  >
                    <Package className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeletingPO(po)}
                    disabled={po.status === 'received'}
                    title="Delete"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ),
            },
          ]}
          data={filteredPOs}
          loading={false}
          getItemId={(po) => po.po_id!}
          actionsColumn={false}
        />
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No purchase orders found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first purchase order to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit PO Modal */}
      <Dialog open={isCreateOpen || !!editingPO} onOpenChange={(open) => {
        if (!open) {
          setIsCreateOpen(false);
          setEditingPO(null);
          setPoFormItems([]);
        }
      }}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingPO ? `Edit Purchase Order: ${editingPO.po_number}` : 'Create New Purchase Order'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex gap-4 py-4">
            {/* Main form area */}
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* General tab content */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">General Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Hospital ID <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      value={formHospitalId}
                      onChange={(e) => setFormHospitalId(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Vendor <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formVendorId}
                      onChange={(e) => setFormVendorId(e.target.value)}
                      required
                      className="w-full h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select vendor...</option>
                      {vendorOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Select the vendor for this purchase order</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Order Date <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="date"
                      value={formOrderDate}
                      onChange={(e) => setFormOrderDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Expected Delivery Date
                    </label>
                    <Input
                      type="date"
                      value={formDeliveryDate}
                      onChange={(e) => setFormDeliveryDate(e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      value={formNotes}
                      onChange={(e) => setFormNotes(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Additional notes (optional)"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Terms and Conditions
                    </label>
                    <textarea
                      rows={6}
                      value={formTerms || DEFAULT_TERMS}
                      onChange={(e) => setFormTerms(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      placeholder="Enter terms and conditions..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      These terms will be included in the purchase order.
                    </p>
                  </div>
                </div>
              </div>

              {/* Items tab */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Purchase Order Items</h3>
                <POItemsTab
                  items={poFormItems}
                  onItemsChange={setPoFormItems}
                  hospitalId={hospitalId}
                />
              </div>
            </div>

            {/* Right sidebar - Totals */}
            <div className="w-80 overflow-y-auto">
              <POTotalsSummary items={poFormItems} />

              <div className="mt-4 space-y-2">
                <Button
                  className="w-full"
                  onClick={async () => {
                    const formData: POFormState = {
                      hospital_id: formHospitalId,
                      vendor_id: formVendorId,
                      order_date: formOrderDate,
                      delivery_date: formDeliveryDate || undefined,
                      notes: formNotes || undefined,
                      items: poFormItems,
                    };

                    if (editingPO) {
                      await handleUpdate(formData);
                    } else {
                      await handleCreate(formData);
                    }
                  }}
                >
                  {editingPO ? 'Update Purchase Order' : 'Create Purchase Order'}
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setIsCreateOpen(false);
                    setEditingPO(null);
                    setPoFormItems([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receiving Modal */}
      <ReceivingModal
        open={!!receivingPO}
        onOpenChange={(open) => !open && setReceivingPO(null)}
        purchaseOrder={receivingPO}
        onReceive={handleReceive}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deletingPO}
        onOpenChange={(open) => !open && setDeletingPO(null)}
        title="Delete Purchase Order"
        description={`Are you sure you want to delete purchase order "${deletingPO?.po_number}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
      />

      {/* View PO Modal */}
      <Dialog open={!!viewingPO} onOpenChange={(open) => !open && setViewingPO(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Purchase Order Details: {viewingPO?.po_number}</DialogTitle>
          </DialogHeader>
          {viewingPO && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Vendor</p>
                  <p className="font-medium">{getVendorName(viewingPO.vendor_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <POStatusBadge status={viewingPO.status as POStatus} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Order Date</p>
                  <p className="font-medium">{viewingPO.order_date || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Delivery Date</p>
                  <p className="font-medium">{viewingPO.delivery_date || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="font-medium font-mono text-lg">{formatCurrency(viewingPO.total_amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Traceability</p>
                  <p className="font-medium capitalize">{viewingPO.traceability_level}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Items ({viewingPO.items.length})</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Item</th>
                        <th className="px-3 py-2 text-right font-medium">Qty Ordered</th>
                        <th className="px-3 py-2 text-right font-medium">Qty Received</th>
                        <th className="px-3 py-2 text-right font-medium">Unit Price</th>
                        <th className="px-3 py-2 text-right font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {viewingPO.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">
                            <p className="font-medium">{item.item_name || `Item ${item.item_id}`}</p>
                            <p className="text-xs text-gray-500">{item.item_code}</p>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{item.quantity_ordered}</td>
                          <td className="px-3 py-2 text-right font-mono">{item.quantity_received || 0}</td>
                          <td className="px-3 py-2 text-right font-mono">{formatCurrency(item.unit_price)}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold">
                            {formatCurrency(item.line_total || (item.quantity_ordered * item.unit_price))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {viewingPO.notes && (
                <div>
                  <h4 className="font-semibold mb-1">Notes</h4>
                  <p className="text-sm text-gray-600">{viewingPO.notes}</p>
                </div>
              )}

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setViewingPO(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert Requisition to PO Dialog */}
      <Dialog open={showConvertRequisition} onOpenChange={(open) => {
        setShowConvertRequisition(open);
        if (!open) setSelectedRequisition('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert Requisition to PO</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select an approved requisition to convert into a Purchase Order.
            </p>
            {approvedRequisitions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No approved requisitions found</p>
                <p className="text-sm">Requisitions must be approved first before conversion</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {approvedRequisitions.map((req) => (
                  <div
                    key={req.requisition_id}
                    className={cn(
                      'p-3 border rounded-lg cursor-pointer transition-colors',
                      selectedRequisition === req.requisition_id
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-gray-50'
                    )}
                    onClick={() => setSelectedRequisition(req.requisition_id || '')}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{req.requisition_number}</p>
                        <p className="text-sm text-gray-500">{req.items.length} items</p>
                      </div>
                      {selectedRequisition === req.requisition_id && (
                        <CheckCircle className="h-5 w-5 text-primary" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => {
                setShowConvertRequisition(false);
                setSelectedRequisition('');
              }}>
                Cancel
              </Button>
              <Button
                disabled={!selectedRequisition}
                onClick={() => {
                  const req = requisitions.find(r => r.requisition_id === selectedRequisition);
                  if (req) {
                    const items: POItemFormState[] = req.items.map(item => ({
                      item_id: item.item_id,
                      item_code: item.item_id,
                      item_name: `Item ${item.item_id}`,
                      quantity_ordered: item.quantity_approved || item.quantity_requested,
                      unit_price: item.unit_cost || 0,
                      unit_of_measure: 'each',
                      line_total: (item.quantity_approved || item.quantity_requested) * (item.unit_cost || 0),
                    }));
                    setPoFormItems(items);
                    setFormHospitalId('550e8400-e29b-41d4-a716-446655440000');
                    setFormVendorId('');
                    setFormOrderDate(new Date().toISOString().split('T')[0]);
                    setFormDeliveryDate('');
                    setFormNotes(`Converted from Requisition: ${req.requisition_number}`);
                    setFormTerms(DEFAULT_TERMS);
                    setShowConvertRequisition(false);
                    setSelectedRequisition('');
                    setIsCreateOpen(true);
                    toast.success('Requisition converted', 'Items have been added. Please select a vendor.');
                  }
                }}
              >
                Convert to PO
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
